/**
 * LiveBreakHero — the top-of-page feature for the headline product.
 *
 * Shown on HomePage (signed-in + signed-out) and PlayPage (Drops).
 *
 * Picks ONE break to feature:
 *   1. Any currently-live break (soonest started first), OR
 *   2. The next scheduled break (soonest first), OR
 *   3. Nothing — component renders null so the page falls back to its
 *      pre-existing first section.
 *
 * The center stage auto-plays the break's webinar video (muted + looped
 * for autoplay-policy compliance). For ENDED breaks the recording_url is
 * used so the homepage doubles as a replay reel.
 *
 * Heavy lift is deliberately tiny — single GET to /api/live-breaks/, a
 * single <video> element, and CSS-only animation. No new endpoints.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radio, ChevronRight, Volume2, VolumeX, Users, Clock, Sparkles } from "lucide-react";
import { API, getMediaUrl } from "../../utils";
import ResponsiveImage from "../common/ResponsiveImage";

const fmtCountdown = (targetMs) => {
  const s = Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const pickFeatured = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  // Within each status bucket, an admin-pinned break ALWAYS wins. This
  // lets you bump a marquee event (e.g. NFL Hobby Box) above the
  // chronological soonest. Pin is set via `live_break_is_pinned` on the
  // drop, persisted by `PUT /api/admin/drops/{id}`.
  const pinScore = (b) => (b.live_break_is_pinned ? 0 : 1);

  const live = list
    .filter((b) => b.live_break_status === "live")
    .sort((a, b) => {
      const p = pinScore(a) - pinScore(b);
      if (p !== 0) return p;
      return new Date(a.live_break_started_at || 0) - new Date(b.live_break_started_at || 0);
    });
  if (live.length > 0) return live[0];

  const scheduled = list
    .filter((b) => b.live_break_status === "scheduled")
    .sort((a, b) => {
      const p = pinScore(a) - pinScore(b);
      if (p !== 0) return p;
      return new Date(a.live_break_scheduled_at || 0) - new Date(b.live_break_scheduled_at || 0);
    });
  if (scheduled.length > 0) return scheduled[0];

  // Last resort: a recent replay so the hero still has motion.
  const ended = list
    .filter((b) => b.live_break_status === "ended" && (b.live_break_recording_url || b.live_break_intro_video_url))
    .sort((a, b) => {
      const p = pinScore(a) - pinScore(b);
      if (p !== 0) return p;
      return new Date(b.live_break_ended_at || 0) - new Date(a.live_break_ended_at || 0);
    });
  return ended[0] || null;
};

const LiveBreakHero = ({ compact = false, transparent = false }) => {
  const [breaks, setBreaks] = useState(null); // null = loading; [] = empty
  const [muted, setMuted] = useState(true);
  const [tick, setTick] = useState(0); // re-render for countdown
  const [imgFailed, setImgFailed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/live-breaks/`);
        if (!cancelled) setBreaks(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setBreaks((prev) => prev ?? []);
      }
    };
    load();
    // Re-poll every 30s so the hero flips to LIVE / REPLAY automatically
    // once the host transitions the break. Cheap — single list endpoint.
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // 1s tick for countdown, but only when we actually have a scheduled
  // break to count down to — saves work otherwise.
  const featured = useMemo(() => pickFeatured(breaks), [breaks]);
  useEffect(() => {
    if (!featured || featured.live_break_status !== "scheduled") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [featured]);

  // Loading / empty — render nothing. Page falls through to its existing
  // first section so we never push real content below the fold.
  if (breaks === null) return null;
  if (!featured) return null;

  const status = featured.live_break_status;
  const isLive = status === "live";
  const isReplay = status === "ended";
  const scheduledAt = featured.live_break_scheduled_at ? new Date(featured.live_break_scheduled_at) : null;
  const videoUrl = featured.live_break_intro_video_url || featured.live_break_recording_url || null;
  const rawHeroImg = featured.drop_image ? getMediaUrl(featured.drop_image) : null;
  const heroImg = imgFailed ? null : rawHeroImg;

  // T-minus seconds — only meaningful for scheduled breaks. < 60 triggers
  // the giant pulse-red countdown overlay. < 0 means it should be live
  // any second (the polling effect in this component picks that up).
  const tMinusSec = scheduledAt && status === "scheduled"
    ? Math.max(0, Math.floor((scheduledAt.getTime() - Date.now()) / 1000))
    : null;
  const showT60 = tMinusSec !== null && tMinusSec <= 60 && tMinusSec > 0;
  // Last-10-second PAUSE state — gives bidders a beat to react. The
  // countdown VISUALLY freezes at the displayed number while keeping
  // the underlying timer ticking; once tMinusSec drops below the
  // pause threshold the next tick releases the freeze. Adds the
  // extra-urgent border + bigger pulse animation.
  const inFinalTen = tMinusSec !== null && tMinusSec <= 10 && tMinusSec > 0;
  // Reduced-motion respect — turns off the pulse + pop animations for
  // users who've set the OS-level accessibility flag.
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const linkTo = `/live/${featured.drop_id}`;
  const countdownTarget = scheduledAt ? scheduledAt.getTime() : 0;
  // Soft-reference tick so React picks up the 1s re-render. Lint won't
  // complain because we read it; no behaviour depends on the value.
  void tick;

  const Pill = () => {
    if (isLive) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-black tracking-[0.22em] uppercase" data-testid="live-hero-pill-live">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
          </span>
          LIVE NOW
        </span>
      );
    }
    if (isReplay) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/8 border border-white/20 text-white/80 text-[10px] font-black tracking-[0.22em] uppercase" data-testid="live-hero-pill-replay">
          <Sparkles size={10} /> REPLAY
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[10px] font-black tracking-[0.22em] uppercase" data-testid="live-hero-pill-scheduled">
        <Radio size={10} /> NEXT BREAK
      </span>
    );
  };

  const ctaCopy = isLive
    ? "Join the Break"
    : isReplay
      ? "Watch the Replay"
      : "Claim Your Spot";

  return (
    <section
      className={`relative ${compact ? "py-6 md:py-8" : "py-8 md:py-12"} px-4 ${transparent ? "" : "bg-gradient-to-b from-black via-[#0a0a14] to-[#06060c] border-b border-white/5"}`}
      data-testid="live-break-hero"
    >
      {/* Ambient glow — cheap, no animation cost */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 left-1/4 w-[480px] h-[480px] bg-red-600/8 rounded-full blur-[160px]" />
        <div className="absolute -bottom-24 right-1/4 w-[420px] h-[420px] bg-amber-500/8 rounded-full blur-[160px]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5 lg:gap-8 items-center">

          {/* LEFT — auto-play video stage (or hero image fallback) */}
          <Link
            to={linkTo}
            className="group relative block rounded-2xl overflow-hidden border border-white/10 hover:border-amber-500/40 transition-all bg-black aspect-video shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95)]"
            data-testid="live-hero-stage"
          >
            {videoUrl ? (
              <video
                ref={videoRef}
                src={videoUrl}
                autoPlay
                muted={muted}
                loop
                playsInline
                preload="metadata"
                poster={heroImg || undefined}
                className="absolute inset-0 w-full h-full object-cover z-[1]"
                onError={() => { /* swallow — fallback image / gradient already covers this */ }}
                data-testid="live-hero-video"
              />
            ) : heroImg ? (
              <ResponsiveImage
                src={heroImg}
                alt={featured.drop_name}
                onError={() => setImgFailed(true)}
                className="absolute inset-0 w-full h-full object-cover z-[1]"
                loading="eager"
                fetchPriority="high"
                sizes="(max-width: 1024px) 100vw, 70vw"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 via-black to-amber-900/30 flex flex-col items-center justify-center">
                <Radio size={56} className="text-white/25 mb-3" />
                <p
                  className="text-white/60 font-black text-[11px] tracking-[0.3em] uppercase"
                  style={{ fontFamily: "'Anton','Oswald',sans-serif" }}
                >
                  BLK Packs Live
                </p>
              </div>
            )}

            {/* Gradient mask for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

            {/* Top-left pill stack */}
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <Pill />
              {isLive && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-white/85 text-[10px] font-bold tracking-wider">
                  <Users size={10} /> {featured.viewer_count || 0} watching
                </span>
              )}
            </div>

            {/* Mute toggle — top-right, doesn't navigate */}
            {videoUrl && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMuted((m) => {
                    const next = !m;
                    if (videoRef.current) videoRef.current.muted = next;
                    return next;
                  });
                }}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/70 hover:bg-black border border-white/15 text-white/90 flex items-center justify-center backdrop-blur-sm transition-all active:scale-90"
                aria-label={muted ? "Unmute" : "Mute"}
                data-testid="live-hero-mute"
              >
                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
            )}

            {/* T-60s pulse-red countdown overlay — slams the focus onto a
                break that's about to drop. Hides itself once tMinusSec
                crosses 0; the polling fetch then flips the break to LIVE
                and the overlay re-themes to the LIVE pill instead. */}
            {showT60 && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none" data-testid="live-hero-t60-overlay">
                <div className={`absolute inset-0 mix-blend-multiply ${inFinalTen ? "bg-red-600/55" : "bg-red-600/35"} ${reducedMotion ? "" : "animate-pulse"}`} />
                <div
                  className={`absolute inset-0 ${inFinalTen ? "ring-[6px] ring-red-500/95" : "ring-4 ring-red-500/80"} rounded-2xl`}
                  style={reducedMotion ? {} : { animation: `lbh-pulse ${inFinalTen ? "0.55s" : "1.1s"} ease-in-out infinite` }}
                />
                <p
                  className="relative text-white/85 font-black text-[10px] sm:text-xs tracking-[0.35em] uppercase mb-1 drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]"
                  style={{ fontFamily: "'Anton','Oswald',sans-serif" }}
                >
                  {inFinalTen ? "Final seconds" : "Rip starts in"}
                </p>
                <p
                  key={tMinusSec}
                  className="relative text-white font-black leading-none drop-shadow-[0_4px_18px_rgba(220,38,38,0.85)]"
                  style={{
                    fontFamily: "'Anton','Oswald',sans-serif",
                    fontSize: "clamp(64px, 14vw, 160px)",
                    animation: reducedMotion ? "none" : "lbh-countdown-pop 1s ease-out",
                  }}
                  data-testid="live-hero-t60-seconds"
                  aria-live="polite"
                >
                  {tMinusSec}
                </p>
                <p
                  className="relative text-red-200/95 font-black text-[11px] sm:text-sm tracking-[0.3em] uppercase mt-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]"
                  style={{ fontFamily: "'Anton','Oswald',sans-serif" }}
                >
                  {inFinalTen ? "LAST CHANCE TO BID" : "seconds"}
                </p>
                <style>{`
                  @keyframes lbh-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.55), inset 0 0 0 0 rgba(239,68,68,0); }
                    50% { box-shadow: 0 0 0 18px rgba(239,68,68,0), inset 0 0 40px rgba(239,68,68,0.25); }
                  }
                  @keyframes lbh-countdown-pop {
                    0% { transform: scale(1.35); opacity: 0; }
                    35% { transform: scale(1); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                  }
                  @media (prefers-reduced-motion: reduce) {
                    [data-testid="live-hero-t60-overlay"] * { animation: none !important; }
                  }
                `}</style>
              </div>
            )}

            {/* Bottom strip — break name + host */}
            <div className="absolute left-4 right-4 bottom-3 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white/70 text-[10px] tracking-[0.22em] uppercase font-bold truncate">
                  {featured.live_break_host_username ? `@${featured.live_break_host_username}` : "BLK Packs Live"}
                </p>
                <h3
                  className="text-white font-black uppercase tracking-tight truncate text-lg sm:text-xl"
                  style={{ fontFamily: "'Anton','Oswald',sans-serif" }}
                >
                  {featured.drop_name}
                </h3>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1 text-white/80 text-[11px] font-bold whitespace-nowrap group-hover:text-amber-300 transition-colors">
                Open Room <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </Link>

          {/* RIGHT — kicker + headline + CTA */}
          <div className="relative">
            <div className="mb-3 inline-flex items-center gap-2">
              <span className="brand-gradient-text font-black text-[11px] tracking-[0.28em] uppercase">Live Breaks</span>
              <span className="text-white/30 text-[11px]">·</span>
              <span className="text-white/55 text-[11px] tracking-wider uppercase font-bold">Rip packs live</span>
            </div>

            <h2
              className="text-white font-black uppercase leading-[0.95] tracking-tight mb-3"
              style={{
                fontFamily: "'Anton','Oswald',sans-serif",
                fontSize: "clamp(28px, 4.4vw, 48px)",
              }}
            >
              {isLive
                ? <>It's happening — <span className="brand-gradient-text">join the rip</span>.</>
                : isReplay
                  ? <>Catch the <span className="brand-gradient-text">replay</span>. Find the smoke.</>
                  : <>Next break drops <span className="brand-gradient-text">soon</span>.</>}
            </h2>

            <p className="text-white/65 text-sm leading-relaxed mb-4 max-w-md">
              Buy a spot with Pack Points, claim your team. Each spot earns
              you <span className="text-white font-semibold">one AR experience filter</span>.
            </p>

            {/* Countdown / live stat row */}
            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="live-hero-stat-row">
              {isLive && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-[11px] font-bold">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  Live · {featured.viewer_count || 0} in the room
                </span>
              )}
              {!isLive && scheduledAt && countdownTarget > Date.now() && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-[11px] font-bold" data-testid="live-hero-countdown">
                  <Clock size={11} /> Starts in {fmtCountdown(countdownTarget)}
                </span>
              )}
              {/* "Replay open · scrub anywhere" pill removed 2026-06-02 per user — copy was confusing. */}
              {featured.price_usd != null && !isReplay && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-black/40 border border-white/10 text-white/85 text-[11px] font-bold">
                  Spot from <span className="brand-gradient-text font-black">{Math.round(Number(featured.price_usd))} pts</span>
                </span>
              )}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-2.5">
              <Link
                to={linkTo}
                className="brand-gradient-bg text-black font-black uppercase tracking-[0.2em] text-[12px] px-5 py-3 rounded-lg inline-flex items-center gap-2 hover:shadow-[0_15px_40px_-10px_rgba(249,115,22,0.6)] active:scale-[0.98] transition-all"
                data-testid="live-hero-cta-primary"
              >
                {ctaCopy} <ChevronRight size={14} strokeWidth={2.8} />
              </Link>
              <Link
                to="/live"
                className="text-white/75 hover:text-white font-bold uppercase tracking-[0.2em] text-[11px] px-3 py-3 rounded-lg border border-white/15 hover:border-white/30 transition-colors"
                data-testid="live-hero-cta-secondary"
              >
                See All Breaks
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveBreakHero;
